import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import config from '../../config/appConfig';
import { getBasePathFromUrl, buildRouteWithLang, getLangFromUrl } from '../../constants/routes';
import './Header.css';

const Header: React.FC = () => {
    const { user, isAuthenticated, login, logout } = useAuth();
    const { mode, setMode } = useTheme();
    const { i18n, t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [openLang, setOpenLang] = useState(false);
    const [openTheme, setOpenTheme] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [openUserMenu, setOpenUserMenu] = useState(false);

    const ref = useRef<HTMLDivElement>(null);
    const themeRef = useRef<HTMLDivElement>(null);
    const userMenuRef = useRef<HTMLDivElement>(null);

    const i18nLanguage = (i18n.language || 'en').split('-')[0];
    const langFromPath = getLangFromUrl(location.pathname);
    const lang = langFromPath || (i18nLanguage === 'fr' ? 'fr' : 'en');

    const toggleLang = () => setOpenLang(!openLang);

    const changeLang = (value: string) => {
        setOpenLang(false);

        const nextLang = value === 'fr' ? 'fr' : 'en';
        i18n.changeLanguage(nextLang);

        const basePath = getBasePathFromUrl(location.pathname) || '/';
        const nextPath = buildRouteWithLang(basePath, nextLang);
        if (nextPath !== location.pathname) {
            navigate(
                {
                    pathname: nextPath,
                    search: location.search,
                    hash: location.hash,
                },
                { replace: true }
            );
        }
    };

    const handleLogin = () => {
        const redirectUrl = location.pathname;
        login(redirectUrl);
    };

    const handleLogout = () => {
        logout(location.pathname);
    };

    /* Close dropdown on outside click */
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpenLang(false);
            }
            if (themeRef.current && !themeRef.current.contains(e.target as Node)) {
                setOpenTheme(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
                setOpenUserMenu(false);
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    useEffect(() => {
        setOpenLang(false);
    }, [location.pathname, location.search, location.hash]);

    return (
        <header className="header-container">
            <div className={`header-top-bar${isAuthenticated ? ' logged-in' : ''}`}>
                {/* HAMBURGER (mobile only) */}
                <button
                    className="mobile-menu-btn"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                >
                    {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                </button>

                {/* LEFT */}
                <div className="header-left">
                    <Link to={buildRouteWithLang('/', lang)} className="logo-link">
                        <img src="/logo.webp" alt="Moneris Logo" />
                    </Link>

                    <a href={`${config.docsBaseUrl}${lang === 'fr' ? '/fr' : ''}/docs/introduction`} className="Header-top-link header-nav-link">
                        {t('nav.monerisApi')}
                    </a>
                    <a href={`${config.docsBaseUrlGo}${lang === 'fr' ? '/fr' : ''}/docs/moneris-go-introduction`} className="Header-top-link header-nav-link">
                        {t('nav.monerisGo')}
                    </a>
                    {config.hostedSolutionsUrl && (
                        <a href={`${config.hostedSolutionsUrl}${lang === 'fr' ? '/fr' : ''}`} className="Header-top-link header-nav-link">
                            {t('nav.hostedSolutions')}
                        </a>
                    )}
                </div>

                {/* RIGHT */}
                <div className="header-right">
                    <a href={`${config.docsBaseUrl}${lang === 'fr' ? '/fr' : ''}/docs/contact-us`} className="Header-top-link header-nav-link">
                        {t('nav.contactUs')}
                    </a>
                    <a href={`${config.docsBaseUrl}${lang === 'fr' ? '/fr' : ''}/docs/become-a-partner`} className="Header-top-link header-nav-link">
                        {t('nav.becomePartner')}
                    </a>
                    <a href={`${config.docsLegacyUrl}`} className="Header-top-link header-nav-link">
                        {t('nav.legacyPortal')}
                    </a>

{isAuthenticated ? (
  <>
    {/* ✅ CLIENT CREDENTIALS LINK (only when logged in) */}
    <Link to={buildRouteWithLang('/client-credentials', lang)} className="Header-top-link header-nav-link">
      {t('nav.accessAndCredentials')}
    </Link>

    {/* ✅ USER NAME with dropdown */}
    <div className="user-menu-container header-nav-link" ref={userMenuRef}>
      <button className="user-name-btn" onClick={() => setOpenUserMenu(!openUserMenu)}>
        {(() => {
          const claims = (user as any)?.idTokenClaims;
          const first = claims?.given_name || '';
          const last = claims?.family_name || '';
          const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
          const full = [first, last].filter(Boolean).map(capitalize).join(' ');
          return full || user?.name || 'User';
        })()}
      </button>

      {openUserMenu && (
        <div className="user-dropdown">
          <button onClick={handleLogout} className="user-dropdown-item">
            <svg className="user-dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Log Out
          </button>
        </div>
      )}
    </div>
  </>
) : (
  <button onClick={handleLogin} className="Header-top-link header-nav-link">
    Log In
  </button>
)}

                    {/* ✅ LANGUAGE */}
                    <div className="lang-container" ref={ref}>
                        <button className="lang-btn" onClick={toggleLang}>
                           <svg fill="none" viewBox="0 0 24 24" className="lang-globe-icon" role="img" width="18" height="18">
                             <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10ZM2 12h20"></path>
                             <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10v0Z"></path>
                           </svg>
                            <span>
                                {lang === 'en' ? t('nav.english') : t('nav.french')}
                            </span>
                        </button>

                        {openLang && (
                            <div className="lang-dropdown">
                                <div
                                    onClick={() => changeLang('en')}
                                    className="lang-item"
                                >
                                    English{' '}
                                    {lang === 'en' && (
                                        <CheckIcon fontSize="small" />
                                    )}
                                </div>

                                <div
                                    onClick={() => changeLang('fr')}
                                    className="lang-item"
                                >
                                    Français{' '}
                                    {lang === 'fr' && (
                                        <CheckIcon fontSize="small" />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* THEME TOGGLE DROPDOWN */}
                    <div className="theme-toggle-wrapper" ref={themeRef}>
                        <button className="theme-icon-btn" onClick={() => setOpenTheme(!openTheme)} aria-label="Color scheme">
                            {mode === 'dark' ? (
                                <svg className="theme-toggle-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 14.79C18.8427 16.4922 18.2039 18.1144 17.1582 19.4668C16.1126 20.8192 14.7035 21.8458 13.0957 22.4265C11.4879 23.0073 9.74798 23.1181 8.0795 22.7461C6.41102 22.3741 4.88299 21.5345 3.67423 20.3258C2.46546 19.117 1.62594 17.589 1.25391 15.9205C0.881876 14.252 0.992717 12.5121 1.57346 10.9043C2.1542 9.29651 3.18083 7.88737 4.53321 6.84175C5.8856 5.79614 7.5078 5.15731 9.21 5C8.21341 6.34827 7.73385 8.00945 7.85853 9.68141C7.98322 11.3534 8.70386 12.9251 9.8894 14.1106C11.0749 15.2961 12.6466 16.0168 14.3186 16.1415C15.9906 16.2662 17.6517 15.7866 19 14.79Z"></path>
                                    <path d="M18.3707 1C18.3707 3.22825 16.2282 5.37069 14 5.37069C16.2282 5.37069 18.3707 7.51313 18.3707 9.74138C18.3707 7.51313 20.5132 5.37069 22.7414 5.37069C20.5132 5.37069 18.3707 3.22825 18.3707 1Z"></path>
                                </svg>
                            ) : mode === 'light' ? (
                                <svg className="theme-toggle-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="4"></circle>
                                    <path d="M12 2v2"></path>
                                    <path d="M12 20v2"></path>
                                    <path d="m4.93 4.93 1.41 1.41"></path>
                                    <path d="m17.66 17.66 1.41 1.41"></path>
                                    <path d="M2 12h2"></path>
                                    <path d="M20 12h2"></path>
                                    <path d="m6.34 17.66-1.41 1.41"></path>
                                    <path d="m19.07 4.93-1.41 1.41"></path>
                                </svg>
                            ) : (
                                <svg className="theme-toggle-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v2"></path>
                                    <path d="M14.837 16.385a6 6 0 1 1-7.223-7.222c.624-.147.97.66.715 1.248a4 4 0 0 0 5.26 5.259c.589-.255 1.396.09 1.248.715"></path>
                                    <path d="M16 12a4 4 0 0 0-4-4"></path>
                                    <path d="m19 5-1.256 1.256"></path>
                                    <path d="M20 12h2"></path>
                                </svg>
                            )}
                        </button>
                        {openTheme && (
                            <div className="theme-dropdown">
                                <div className="theme-dropdown-header">{t('theme.colorScheme', { defaultValue: 'Color Scheme' })}</div>
                                <div
                                    className="theme-dropdown-item"
                                    onClick={() => { setMode('system'); setOpenTheme(false); }}
                                >
                                    <span className="theme-dropdown-item-icon">
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2v2"></path>
                                            <path d="M14.837 16.385a6 6 0 1 1-7.223-7.222c.624-.147.97.66.715 1.248a4 4 0 0 0 5.26 5.259c.589-.255 1.396.09 1.248.715"></path>
                                            <path d="M16 12a4 4 0 0 0-4-4"></path>
                                            <path d="m19 5-1.256 1.256"></path>
                                            <path d="M20 12h2"></path>
                                        </svg>
                                    </span>
                                    <span className="theme-dropdown-item-label">{t('theme.system', { defaultValue: 'System' })}</span>
                                    {mode === 'system' && <CheckIcon fontSize="small" className="theme-dropdown-check" />}
                                </div>
                                <div
                                    className="theme-dropdown-item"
                                    onClick={() => { setMode('light'); setOpenTheme(false); }}
                                >
                                    <span className="theme-dropdown-item-icon">
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="4"></circle>
                                            <path d="M12 2v2"></path>
                                            <path d="M12 20v2"></path>
                                            <path d="m4.93 4.93 1.41 1.41"></path>
                                            <path d="m17.66 17.66 1.41 1.41"></path>
                                            <path d="M2 12h2"></path>
                                            <path d="M20 12h2"></path>
                                            <path d="m6.34 17.66-1.41 1.41"></path>
                                            <path d="m19.07 4.93-1.41 1.41"></path>
                                        </svg>
                                    </span>
                                    <span className="theme-dropdown-item-label">{t('theme.light', { defaultValue: 'Light' })}</span>
                                    {mode === 'light' && <CheckIcon fontSize="small" className="theme-dropdown-check" />}
                                </div>
                                <div
                                    className="theme-dropdown-item"
                                    onClick={() => { setMode('dark'); setOpenTheme(false); }}
                                >
                                    <span className="theme-dropdown-item-icon">
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M19 14.79C18.8427 16.4922 18.2039 18.1144 17.1582 19.4668C16.1126 20.8192 14.7035 21.8458 13.0957 22.4265C11.4879 23.0073 9.74798 23.1181 8.0795 22.7461C6.41102 22.3741 4.88299 21.5345 3.67423 20.3258C2.46546 19.117 1.62594 17.589 1.25391 15.9205C0.881876 14.252 0.992717 12.5121 1.57346 10.9043C2.1542 9.29651 3.18083 7.88737 4.53321 6.84175C5.8856 5.79614 7.5078 5.15731 9.21 5C8.21341 6.34827 7.73385 8.00945 7.85853 9.68141C7.98322 11.3534 8.70386 12.9251 9.8894 14.1106C11.0749 15.2961 12.6466 16.0168 14.3186 16.1415C15.9906 16.2662 17.6517 15.7866 19 14.79Z"></path>
                                            <path d="M18.3707 1C18.3707 3.22825 16.2282 5.37069 14 5.37069C16.2282 5.37069 18.3707 7.51313 18.3707 9.74138C18.3707 7.51313 20.5132 5.37069 22.7414 5.37069C20.5132 5.37069 18.3707 3.22825 18.3707 1Z"></path>
                                        </svg>
                                    </span>
                                    <span className="theme-dropdown-item-label">{t('theme.dark', { defaultValue: 'Dark' })}</span>
                                    {mode === 'dark' && <CheckIcon fontSize="small" className="theme-dropdown-check" />}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="header-bottom-strip"></div>

            {/* MOBILE DRAWER */}
            {mobileMenuOpen && (
                <>
                    <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
                    <div className="mobile-drawer">
                        {isAuthenticated ? (
                            <>
                                <Link to="/client-credentials" className="mobile-drawer-link" onClick={() => setMobileMenuOpen(false)}>
                                    {t('nav.accessAndCredentials')}
                                </Link>
                                <div className="mobile-drawer-username">
                                    {(() => {
                                        const claims = (user as any)?.idTokenClaims;
                                        const first = claims?.given_name || '';
                                        const last = claims?.family_name || '';
                                        const full = [first, last].filter(Boolean).join(' ');
                                        return full || user?.name || 'User';
                                    })()}
                                </div>
                                <button onClick={handleLogout} className="mobile-drawer-link">
                                    Log Out
                                </button>
                            </>
                        ) : (
                            <button onClick={handleLogin} className="mobile-drawer-link">
                                Log In
                            </button>
                        )}

                        <div className="mobile-drawer-logo">
                            <img src="/logo.webp" alt="Moneris Logo" />
                        </div>
                    </div>
                </>
            )}
        </header>
    );
};

export default Header;
